import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, User as UserIcon, Lock, Droplet } from 'lucide-react';
import { Card, Badge, Button, Input, Alert, Spinner, useIsMobile } from '../components/ui';
import LocationSelect from '../components/ui/LocationSelect';
import PhoneInput from '../components/ui/PhoneInput';
import PageLayout from '../components/layout/PageLayout';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function AccountSettings() {
  const { user, updateUser, becomeDonor } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [upgradeMessage, setUpgradeMessage] = useState(null);
  const [upgrading, setUpgrading] = useState(false);

  const [profileForm, setProfileForm] = useState({ name: '', email: '', phone: '' });
  const [profileMessage, setProfileMessage] = useState(null);
  const [phoneError, setPhoneError] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const [pickedPlace, setPickedPlace] = useState(null);
  const [locMessage, setLocMessage] = useState(null);
  const [locatingGPS, setLocatingGPS] = useState(false);
  const [savingLoc, setSavingLoc] = useState(false);

  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwMessage, setPwMessage] = useState(null);
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    if (user) {
      setProfileForm({ name: user.name || '', email: user.email || '', phone: user.phone || '' });
    }
  }, [user]);

  if (!user) return (
    <PageLayout>
      <div style={{ display: 'flex', justifyContent: 'center', padding: '80px' }}><Spinner /></div>
    </PageLayout>
  );

  const saveProfile = async () => {
    // Caught here rather than as a 422 from the server: a half-typed number is
    // an ordinary mistake, and Twilio can't reach anything that isn't E.164.
    if (!/^\+[1-9]\d{6,14}$/.test(profileForm.phone)) {
      setPhoneError('Enter a valid phone number, including its country code.');
      return;
    }
    setPhoneError('');
    setSavingProfile(true);
    setProfileMessage(null);
    try {
      const { data } = await api.patch('/auth/me', profileForm);
      updateUser(data);
      setProfileMessage({ variant: 'success', text: 'Profile updated. Check your inbox if your email changed — you\'ll need to re-verify it.' });
    } catch (e) {
      setProfileMessage({ variant: 'error', text: e.response?.data?.detail || 'Could not update profile.' });
    } finally {
      setSavingProfile(false);
    }
  };

  const upgradeToDonor = async () => {
    setUpgrading(true);
    setUpgradeMessage(null);
    try {
      await becomeDonor();
      // Straight into the donor questionnaire — the account is a donor account
      // now, but it can't be matched to anything until the profile exists.
      navigate('/donor/setup?redirect=/donor/profile');
    } catch (e) {
      setUpgradeMessage({ variant: 'error', text: e.response?.data?.detail || 'Could not switch your account to a donor account.' });
      setUpgrading(false);
    }
  };

  // Returns the saved user so callers can report what actually landed —
  // a GPS save has its city/state filled in server-side.
  const saveLocation = async (patch) => {
    try {
      const { data } = await api.patch('/auth/me/location', patch);
      updateUser(data);
      return data;
    } catch (e) {
      setLocMessage({ variant: 'error', text: e.response?.data?.detail || 'Could not update location.' });
      return null;
    }
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocMessage({ variant: 'error', text: 'Geolocation is not supported by your browser. Pick your city below instead.' });
      return;
    }
    setLocatingGPS(true);
    setLocMessage(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const data = await saveLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
        if (data) {
          const place = [data.city, data.state, data.country].filter(Boolean).join(', ');
          setLocMessage(place
            ? { variant: 'success', text: `Location updated — ${place}.` }
            : { variant: 'warning', text: "GPS location saved, but we couldn't work out which city it's in. Pick one below so city-based searches still find you." });
        }
        setLocatingGPS(false);
      },
      () => {
        setLocMessage({ variant: 'error', text: 'Location access denied. Pick your city below instead.' });
        setLocatingGPS(false);
      },
      { timeout: 10000 }
    );
  };

  const saveManualLocation = async () => {
    if (!pickedPlace) {
      setLocMessage({ variant: 'error', text: 'Pick a country, region and city first.' });
      return;
    }
    setSavingLoc(true);
    setLocMessage(null);
    // Only the id goes up: the server re-reads the city's own coordinates from
    // it, so a tampered or stale lat/lng from the browser can't misplace anyone.
    const data = await saveLocation({ city_id: pickedPlace.city_id });
    if (data) setLocMessage({ variant: 'success', text: 'Location updated.' });
    setSavingLoc(false);
  };

  const changePassword = async () => {
    setPwMessage(null);
    if (pwForm.new_password !== pwForm.confirm_password) {
      setPwMessage({ variant: 'error', text: 'New passwords do not match.' });
      return;
    }
    setSavingPw(true);
    try {
      await api.post('/auth/me/change-password', {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      setPwMessage({ variant: 'success', text: 'Password updated.' });
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (e) {
      setPwMessage({ variant: 'error', text: e.response?.data?.detail || 'Could not update password.' });
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <PageLayout>
      <div style={{ animation: 'bb-fadeUp 0.5s ease both' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--gray-900)', marginBottom: '16px' }}>
          Account Settings
        </h1>

        {/* Profile details */}
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <UserIcon size={16} color="var(--red)" />
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--gray-900)' }}>Profile Details</div>
          </div>
          <p style={{ fontSize: '12.5px', color: 'var(--gray-500)', marginBottom: '14px', lineHeight: 1.5 }}>
            Changing your email or phone will require re-verification.
          </p>

          <div style={{ display: 'flex', gap: '6px', marginBottom: '14px' }}>
            <Badge color={user.is_email_verified ? 'green' : 'red'}>{user.is_email_verified ? 'Email verified' : 'Email not verified'}</Badge>
            <Badge color={user.is_phone_verified ? 'green' : 'red'}>{user.is_phone_verified ? 'Phone verified' : 'Phone not verified'}</Badge>
          </div>

          {profileMessage && <Alert variant={profileMessage.variant} style={{ marginBottom: '14px' }}>{profileMessage.text}</Alert>}

          <Input
            label="Full name"
            value={profileForm.name}
            onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))}
          />
          <Input
            label="Email"
            type="email"
            value={profileForm.email}
            onChange={e => setProfileForm(f => ({ ...f, email: e.target.value }))}
          />
          <PhoneInput
            value={profileForm.phone}
            onChange={v => setProfileForm(f => ({ ...f, phone: v }))}
            error={phoneError}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="primary" loading={savingProfile} onClick={saveProfile} style={isMobile ? { width: '100%' } : {}}>
              Save changes
            </Button>
          </div>
        </Card>

        {/* Become a donor — recipients only; there's nothing to switch back to,
            since a donor account can post requests just the same. */}
        {user.role === 'recipient' && (
          <Card style={{ marginTop: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <Droplet size={16} color="var(--red)" />
              <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--gray-900)' }}>Become a Donor</div>
            </div>
            <p style={{ fontSize: '12.5px', color: 'var(--gray-500)', marginBottom: '14px', lineHeight: 1.5 }}>
              Want to donate as well as request? No new account needed — we'll switch this one over
              and take you through the short donor profile (blood type, age, weight, health screening).
              You'll keep every request you've already posted.
            </p>

            {upgradeMessage && <Alert variant={upgradeMessage.variant} style={{ marginBottom: '14px' }}>{upgradeMessage.text}</Alert>}

            <Button variant="primary" loading={upgrading} onClick={upgradeToDonor} style={isMobile ? { width: '100%' } : {}}>
              Become a donor →
            </Button>
          </Card>
        )}

        {/* Location */}
        <Card style={{ marginTop: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <MapPin size={16} color="var(--red)" />
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--gray-900)' }}>Location</div>
          </div>
          <p style={{ fontSize: '12.5px', color: 'var(--gray-500)', marginBottom: '16px', lineHeight: 1.5 }}>
            We use this to alert you about nearby blood requests. Only your city is shown to others.
          </p>

          <div style={{
            padding: '11px 14px', borderRadius: '9px', background: 'var(--gray-50)',
            border: '1px solid var(--gray-100)', marginBottom: '16px', fontSize: '13.5px',
          }}>
            <span style={{ color: 'var(--gray-500)' }}>Current: </span>
            <span style={{ fontWeight: 600, color: 'var(--gray-900)' }}>
              {[user.city, user.state, user.country].filter(Boolean).join(', ') || 'Not set'}
            </span>
            {user.latitude != null && (
              <span style={{ color: 'var(--green-dark)', marginLeft: '8px', fontSize: '12px' }}>· coordinates saved</span>
            )}
          </div>

          {/* A location with no coordinates is invisible to nearby-request
              matching, which is the whole point of storing one — so say so
              rather than letting it look saved. */}
          {user.latitude == null && (
            <Alert variant="warning">
              We can't alert you about nearby requests until your location has coordinates —
              share your GPS or pick your city below.
            </Alert>
          )}

          {locMessage && <Alert variant={locMessage.variant}>{locMessage.text}</Alert>}

          <Button variant="primary" loading={locatingGPS} onClick={useCurrentLocation} style={{ marginBottom: '18px' }}>
            📍 Use my current location
          </Button>

          <LocationSelect
            value={{ country_id: user.country_id, state_id: user.state_id, city_id: user.city_id }}
            onChange={setPickedPlace}
          />
          <Button variant="secondary" loading={savingLoc} onClick={saveManualLocation} disabled={!pickedPlace}>
            Save location
          </Button>
        </Card>

        {/* Password */}
        <Card style={{ marginTop: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
            <Lock size={16} color="var(--red)" />
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--gray-900)' }}>Change Password</div>
          </div>

          {pwMessage && <Alert variant={pwMessage.variant} style={{ marginBottom: '14px' }}>{pwMessage.text}</Alert>}

          <Input
            label="Current password"
            type="password"
            value={pwForm.current_password}
            onChange={e => setPwForm(f => ({ ...f, current_password: e.target.value }))}
          />
          <Input
            label="New password"
            type="password"
            value={pwForm.new_password}
            onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))}
          />
          <Input
            label="Confirm new password"
            type="password"
            value={pwForm.confirm_password}
            onChange={e => setPwForm(f => ({ ...f, confirm_password: e.target.value }))}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="primary" loading={savingPw} onClick={changePassword} style={isMobile ? { width: '100%' } : {}}>
              Update password
            </Button>
          </div>
        </Card>
      </div>
    </PageLayout>
  );
}
